import {
  LocalStorageSaveAdapter,
  WriteBehindSaveAdapter,
  type SaveAdapter,
} from "@yjsgames/save";
import type { Firestore } from "firebase/firestore";
import { FirestoreSaveAdapter } from "./FirestoreSaveAdapter.js";

export interface SaveAdapterConfig {
  /** 네이티브(Capacitor) 빌드 여부 — 현재는 로컬 계층 선택 대신 호출자 의도를 문서화한다. */
  isNative: boolean;
  /** Firestore + 익명 UID. null이면 클라우드 비활성(로컬 단독 세이브). */
  cloud: { db: Firestore; uid: string } | null;
  /** 클라우드 flush 주기(ms). 기본 60s. */
  flushIntervalMs?: number;
}

/**
 * "로컬이냐 클라우드냐"를 외부(부팅)에서 결정하는 SaveAdapter 팩토리.
 * 게임 코드(ModuleBrickSaveSystem)는 반환된 SaveAdapter(읽기/쓰기 추상)만 의존하고 조합은 모른다.
 *
 * - 로컬 계층: WebView localStorage
 * - cloud 있음: WriteBehind(로컬 즉시 + n분 batch flush, FirestoreSaveAdapter를 cloud 계층으로)
 * - cloud 없음: 로컬 단독 (Firebase 미설정/오프라인/익명인증 실패 시 graceful fallback)
 */
/**
 * 로컬 전용 SaveAdapter (클라우드 동기화 제외) — 기기별 설정(BrickSettings) 저장용.
 * 네이티브에서도 localStorage를 쓴다. 일부 에뮬레이터에서 @capacitor/preferences 응답이 멈추면
 * 뒤따르는 광고/IAP 네이티브 플러그인 호출까지 막히므로, 부팅 필수 저장 경로는 JS 저장소로 유지한다.
 * 진행 데이터와 달리 WriteBehind(클라우드)로 감싸지 않는다.
 */
export function createLocalSaveAdapter(_isNative: boolean): SaveAdapter {
  return new LocalStorageSaveAdapter();
}

export function createSaveAdapter(config: SaveAdapterConfig): SaveAdapter {
  const local = createLocalSaveAdapter(config.isNative);
  if (!config.cloud) return local;
  const cloud = new FirestoreSaveAdapter(config.cloud.db, config.cloud.uid);
  return new WriteBehindSaveAdapter(local, cloud, {
    flushIntervalMs: config.flushIntervalMs ?? 60_000,
  });
}
